import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import GraphemeSplitter from 'grapheme-splitter';
import { useTranslation } from 'react-i18next';
import { useLocation, useSearchParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import InputStateFixedContainer from '@pkg/components/YatPicker/InputStateToggles/InputStateFixedContainer';
import InputStateFocusButton from '@pkg/components/YatPicker/InputStateToggles/InputStateFocusButton';
import { useAppDispatch, useAppSelector } from '@pkg/hooks';

import { apiFactory, Emojis, Users } from '@app/client';
import config from '@app/config';
import { useWindowResize } from '@app/helpers';
import { RoutingSearchParams } from '@app/routes/types';
import { setAuthTokens, useLogout } from '@app/store/actions/auth';
import { addNotification } from '@app/store/actions/notification';
import { setUserPartnerAddresses } from '@app/store/actions/partner-integrations';
import { addPreEmojis } from '@app/store/actions/picker';
import { initializeUser } from '@app/store/actions/user';
import { type Emoji } from '@app/store/types/picker';

import { YatModalBottomSheet } from '../ModalBottomSheet';
import { YatModalSideSheet } from '../ModalSideSheet';
import { RhythmScoreModal } from '../RhythmScoreModal';

import BackButton from './BackButton/BackButton';
import './BackButton/BackButton.scss';
import Cart from './Cart/Cart';
import CartButton from './CartButton/CartButton';
import EmojiSlider from './EmojiSlider';
import PageHeader from './PageHeader';
import PickerForm from './PickerForm';
import PickerResult from './PickerResult';
import './YatPicker.scss';

const min = config.minLengthYatForSale;
const max = config.maxLengthYatForSale;

// The string that tells us to generate a random yat.
const GET_RANDOM_YAT_TRIGGER = 'random';
const PRE_YAT_QUERY = 'yat';

const graphemeSplitter = new GraphemeSplitter();

const YatPicker = () => {
    const { t } = useTranslation(['yat_picker']);
    const { state: locationState } = useLocation();
    const nodeRef = useRef(null);

    const alertVisible = useAppSelector(({ alert }) => alert.visible);
    const isRegisteredUser = useAppSelector((rootState) => rootState.user.isRegisteredUser);
    const userId = useAppSelector((rootState) => rootState.user.userAccount?.user.id);
    const partnerConversionId = useAppSelector((rootState) => rootState.partnerIntegrations.partnerConversionId);
    const walletAddresses = useAppSelector((rootState) => rootState.partnerIntegrations.walletAddresses);
    const { inputFocused, tiles, searchResult, orderItems, rhythmScore, rhythmScoreCopy } = useAppSelector(
        ({ picker }) => picker,
    );

    const [isGettingRandomYat, setIsGettingRandomYat] = useState(false);
    const [RSInfoModalOpen, setRSInfoModalOpen] = useState(false);
    const [cartModalOpen, setCartModalOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(true);

    const [walletKey, setWalletKey] = useState<string>();
    const [enableGoBack, setEnableGoBack] = useState<boolean>(false);

    const [searchParams] = useSearchParams();

    const isTouch = !window.matchMedia('(pointer: fine)').matches;
    const windowSize = useWindowResize();
    const dispatch = useAppDispatch();

    const preYat = useMemo(() => searchParams.get(PRE_YAT_QUERY), [searchParams]);

    const { random: getRandomEmojiURL } = Emojis.Emoji;

    useEffect(() => {
        locationState?.walletLinkCreation && setWalletKey(locationState.walletLinkCreation);
        setEnableGoBack(locationState?.enableGoBack);
    }, [locationState]);

    useEffect(() => {
        // TODO: should this  be fetched once at root level?
        // If not user Id set register an empty user
        if (userId) return;

        apiFactory()
            .call(Users.registerEmptyUser, { value: { partner_conversion_id: partnerConversionId } })
            .then((r) => {
                if (r.ok) {
                    dispatch(setAuthTokens(r.value, true));
                    dispatch(initializeUser());
                    if (walletAddresses) {
                        dispatch(setUserPartnerAddresses(walletAddresses));
                    }
                    return;
                } else {
                    dispatch(addNotification({ message: r.error as string, severity: 'error' }));
                    return Promise.reject(false);
                }
            });
    }, [userId, dispatch, partnerConversionId, walletAddresses]);

    const setPreEmoji = useCallback(
        (emojiId: string) => {
            const split = graphemeSplitter.splitGraphemes(emojiId);

            const arr: Emoji[] = [];

            for (let i = 0; i < split.length; i++) {
                const char = split[i];
                if (char !== '') {
                    arr.push([char]);
                }
            }

            dispatch(addPreEmojis(arr));
        },
        [dispatch],
    );

    const getRandomEmoji = useCallback(async () => await apiFactory().call(getRandomEmojiURL, {}), [getRandomEmojiURL]);

    useEffect(() => {
        if (!preYat?.length) {
            return;
        }
        const decoded = decodeURI(preYat as string);
        if (decoded === GET_RANDOM_YAT_TRIGGER) {
            setIsGettingRandomYat(true);
            (async () => {
                const preEmojiRes = await getRandomEmoji();
                if (preEmojiRes?.ok) {
                    const emojiId = preEmojiRes.value.result?.[0].emoji_id;
                    if (emojiId) {
                        setPreEmoji(emojiId);
                        setIsGettingRandomYat(false);
                    }
                }
            })();
        }
    }, [getRandomEmoji, preYat, setPreEmoji]);

    useEffect(() => onResize(), []);

    useEffect(() => {
        if (windowSize !== undefined) onResize();
    }, [windowSize]);

    const onResize = () => {
        setIsMobile(window.matchMedia('(max-width: 768px)').matches);
    };
    const logout = useLogout();
    const onBack = useCallback(() => {
        if (!isRegisteredUser) {
            logout(walletKey?.length ? `/checkout/?${RoutingSearchParams.PartnerKey}=${walletKey}` : undefined);
        }
    }, [isRegisteredUser, logout, walletKey]);

    useEffect(() => {
        if (orderItems.length === 0) {
            setCartModalOpen(false);
        }
    }, [orderItems.length]);

    const renderBackButton = useMemo(() => {
        const backButtonState = walletKey || enableGoBack ? null : { walletLinkCreation: walletKey };

        const BackButtonTo = walletKey || (enableGoBack && (!isTouch || !isMobile)) ? 'goBack' : '/';
        return (
            <BackButton
                to={BackButtonTo}
                state={backButtonState}
                icon="close"
                ariaLabel={t('common:back')}
                onClick={onBack}
            />
        );
    }, [walletKey, enableGoBack, isTouch, isMobile, t, onBack]);

    const renderCartButton = useMemo(() => {
        return (
            <CSSTransition nodeRef={nodeRef} in={orderItems.length > 0} timeout={250} classNames="fade-base">
                <CartButton
                    onClick={() => {
                        setCartModalOpen((c) => !c);
                    }}
                    cartItems={orderItems.length}
                />
            </CSSTransition>
        );
    }, [orderItems]);

    return (
        <div className={`yat-picker${searchResult.length > 0 ? ' yat-picker--is-searching' : ''}`}>
            <div className="yat-picker__top">
                <PageHeader title={t('create_your_yat')} subtitle={t('pick_at_least_3_emojis')} />
                {renderBackButton}
                {renderCartButton}
            </div>
            {!isGettingRandomYat && (
                <div className="yat-picker__form">
                    <PickerForm placeholder={t('search_for_emojis')} minLength={min} maxLength={max} />
                </div>
            )}
            <div className="yat-picker__result">
                <PickerResult
                    walletKey={walletKey}
                    onRhythmScoreClick={() => {
                        setRSInfoModalOpen(true);
                    }}
                />
            </div>
            {!isGettingRandomYat && (
                <div className="yat-picker__emoji-slider">
                    <div className="yat-picker__emoji-slider-focus-button">
                        {isTouch && !inputFocused && tiles.length === 0 && <InputStateFocusButton />}
                    </div>
                    {!alertVisible && <EmojiSlider disabled={tiles.length >= max} />}
                </div>
            )}
            <div className="yat-picker__input-state-fixed-container">
                {isTouch && inputFocused && tiles.length === 0 && <InputStateFixedContainer />}
            </div>
            <div className="yat-picker__rs-modal">
                {isMobile ? (
                    <YatModalBottomSheet
                        open={RSInfoModalOpen}
                        modalLabelId="yat-picker-cart-title"
                        closeCallback={() => setRSInfoModalOpen(false)}
                    >
                        <RhythmScoreModal rhythmScore={rhythmScore} rhythmScoreCopy={rhythmScoreCopy} />
                    </YatModalBottomSheet>
                ) : (
                    <YatModalSideSheet
                        open={RSInfoModalOpen}
                        side="right"
                        modalLabelId="yat-picker-rs-modal-title"
                        closeCallback={() => setRSInfoModalOpen(false)}
                    >
                        <RhythmScoreModal rhythmScore={rhythmScore} rhythmScoreCopy={rhythmScoreCopy} />
                    </YatModalSideSheet>
                )}
            </div>
            <div className="yat-picker__cart">
                {isMobile ? (
                    <YatModalBottomSheet
                        open={cartModalOpen}
                        modalLabelId="yat-picker-cart-title"
                        closeCallback={() => setCartModalOpen(false)}
                        scrollable={false}
                    >
                        <Cart />
                    </YatModalBottomSheet>
                ) : (
                    <YatModalSideSheet
                        open={cartModalOpen}
                        side="right"
                        modalLabelId="yat-picker-cart-title"
                        closeCallback={() => setCartModalOpen(false)}
                        scrollable={false}
                    >
                        <Cart />
                    </YatModalSideSheet>
                )}
            </div>
        </div>
    );
};

export default YatPicker;
